import React, { useState, useEffect, useRef } from 'react';
import { User, Lock, LogOut } from '../icons';
import { ProfileModal } from '../ProfileModal';
import type { User as ProfileUser } from '../ProfileModal';
import Tooltip from './Tooltip';

interface UserMenuProps {
  user: ProfileUser | null;
  onLogout: () => void | Promise<void>;
  onChangePassword: () => void;
}

/**
 * UserMenu component - part of unified TopBar
 */
export const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout, onChangePassword }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside the dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    // Small delay to avoid immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when profile modal opens
  useEffect(() => {
    if (showProfile) {
      setIsOpen(false);
    }
  }, [showProfile]);

  const getInitials = () => {
    if (!user) return '?';
    const firstName = user.imie || '';
    const lastName = user.nazwisko || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  };

  const getFullName = () => {
    if (!user) return 'Użytkownik';
    return `${user.imie || ''} ${user.nazwisko || ''}`.trim() || 'Użytkownik';
  };

  const handleLogout = async () => {
    try {
      await onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* User Avatar Button */}
        <Tooltip content={getFullName()} position="bottom">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="flex items-center justify-center hover:text-accent transition-colors p-1 cursor-pointer"
            type="button"
            aria-label={getFullName()}
          >
            <User className="w-5 h-5 text-text" />
          </button>
        </Tooltip>

        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className="absolute right-0 mt-2 bg-white border border-border rounded-lg shadow-lg z-[9998]" 
            style={{ backgroundColor: '#ffffff', width: '240px', minWidth: '240px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* User Info Section */}
            <div className="px-4 py-3 border-b border-border">
              <div className="flex flex-col items-center gap-2 text-center">
                <div 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  {getInitials()}
                </div>
                <div style={{ width: '100%' }}>
                  <p className="text-sm font-semibold text-text" style={{ whiteSpace: 'nowrap' }}>{getFullName()}</p>
                  {user?.role_names && user.role_names.length > 0 && (
                    <p className="text-xs text-muted mt-0.5" style={{ whiteSpace: 'nowrap' }}>{user.role_names.join(', ')}</p>
                  )}
                  {user?.email && (
                    <p className="text-xs text-muted mt-0.5" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              {/* Profile */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowProfile(true);
                }}
                className="w-full px-4 py-2 text-left text-sm text-text hover:bg-bg flex items-center gap-3 transition-colors cursor-pointer"
                style={{ whiteSpace: 'nowrap' }}
              >
                <User className="w-4 h-4 flex-shrink-0" />
                <span>Mój profil</span>
              </button>

              {/* Change Password */}
              <button
                onClick={() => {
                  setIsOpen(false);
                  onChangePassword();
                }}
                className="w-full px-4 py-2 text-left text-sm text-text hover:bg-bg flex items-center gap-3 transition-colors cursor-pointer"
                style={{ whiteSpace: 'nowrap' }}
              >
                <Lock className="w-4 h-4 flex-shrink-0" />
                <span>Zmień hasło</span>
              </button>
            </div>

            {/* Logout */}
            <div className="border-t border-border mt-1 pt-1">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors cursor-pointer"
                style={{ whiteSpace: 'nowrap' }}
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span>Wyloguj się</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfile} 
        onClose={() => {
          setShowProfile(false);
          setIsOpen(false); // Ensure dropdown is closed when modal closes
        }} 
        user={user} 
      />
    </>
  );
};

